import { Trans, useLingui } from '@lingui/react';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { Alert } from '@startuptools/ui/Alert';
import { Button } from '@startuptools/ui/Button';
import { Sheet } from '@startuptools/ui/Sheet';
import { Stack } from '@startuptools/ui/Stack';
import { Table } from '@startuptools/ui/Table';
import { DateFormat } from '@startuptools/ui/DateFormat';
import { NumberFormat } from '@startuptools/ui/NumberFormat';
import {
  useDocGenIndividualReportMutation,
  useIndividualReportSuspenseQuery,
} from '../../../../graphql/react-operations';
import { Suspense, useState } from 'react';
import { type DateTime } from 'luxon';
import { DateInput } from '@startuptools/ui/DateInput';
import { PDFButton } from '@startuptools/business-ui/document/PDFPreviewButton';
import { apiClient } from '../../../../common/http';

export const IndividualReportShTable = ({ shareholderId }: { shareholderId: string }) => {
  const companyId = useCompanyId();
  const { i18n } = useLingui();
  const [date, setDate] = useState<DateTime | null>(null);

  // eslint-disable-next-line sut/loading-mutation
  const [doExport] = useDocGenIndividualReportMutation();

  const handleExportPdf = async (docLanguage: { primaryLang: boolean; secondaryLang: boolean }) => {
    const { data } = await doExport({
      variables: {
        companyId,
        shareholderId,
        date: date ? date.toUTC(0, { keepLocalTime: true }).toISODate() : undefined,
        docLanguage: { primary_lang: docLanguage.primaryLang, secondary_lang: docLanguage.secondaryLang },
      },
    });
    if (!data) {
      throw new Error('doh');
    }
    const dg = data.docGenIndividualReport.docGen;
    const { filename } = dg.docGenDetails;
    if (!filename) {
      throw new Error('no filename');
    }
    const blob = await apiClient.preview2({
      filename,
      docGens: [dg],
    });
    return { filename, blob };
  };

  return (
    <Stack gap={1}>
      <Stack direction="row" gap={1}>
        <DateInput
          sx={{ width: '210px' }}
          label={<Trans id="Show until date" />}
          value={date}
          onChange={value => setDate(value)}
          slotProps={{ field: { clearable: true } }}
        />
        <PDFButton
          label={i18n.t('Export as PDF')}
          button={({ label, ...props }) => (
            <Button variant="outlined" name={label} {...props}>
              <Trans id="Export as PDF" />
            </Button>
          )}
          generatePdf={handleExportPdf}
        />
      </Stack>

      <Suspense>
        <IndividualReportShTableInner shareholderId={shareholderId} date={date} />
      </Suspense>

      {/* {anchorEl && <PdfPreview anchorEl={anchorEl} onClose={() => setAnchorEl(null)} docGen={handleExportPdf} />} */}
    </Stack>
  );
};

const IndividualReportShTableInner = ({
  shareholderId,
  date,
}: {
  shareholderId: string;
  date: DateTime | null | undefined;
}) => {
  const { i18n } = useLingui();
  const companyId = useCompanyId();

  const {
    data: { individualReport },
  } = useIndividualReportSuspenseQuery({
    variables: {
      companyId,
      input: { shareholderId, date: date ? date.toUTC(0, { keepLocalTime: true }).toISODate() : undefined },
    },
    // Remove once the ShareRegister runs in React and we can trigger refetch on update
    fetchPolicy: 'no-cache',
  });

  return (
    <Stack gap={2}>
      {individualReport.shareClasses.length === 0 && (
        <Alert>
          <Trans id="We could not find any transactions for this shareholder" />
        </Alert>
      )}
      {individualReport.shareClasses.map(sc => {
        return (
          <Sheet key={sc.shareClassName} variant="soft">
            <Stack gap={1}>
              <Table
                noWrap
                sx={theme => ({
                  '.buy': { backgroundColor: theme.palette.primary.softBg },
                  '.sale': { backgroundColor: theme.palette.warning.softBg },
                  tableLayout: 'auto',
                })}
              >
                <thead>
                  <tr>
                    <th colSpan={3}>{sc.shareClassName}</th>
                    <th className="buy" colSpan={2}>
                      <Trans id="Buy" />
                    </th>
                    <th className="sale" colSpan={2}>
                      <Trans id="Sale" />
                    </th>
                    <th />
                  </tr>
                  <tr>
                    <td>
                      <Trans id="Date" />
                    </td>
                    <td>
                      <Trans id="Transaction Type" />
                    </td>
                    <td align="right">
                      <Trans id="Number of Shares" />
                    </td>
                    <td align="right" className="buy">
                      <Trans id="Price per Share" />
                    </td>
                    <td align="right" className="buy">
                      <Trans id="Sum" />
                    </td>
                    <td align="right" className="sale">
                      <Trans id="Price per Share" />
                    </td>
                    <td align="right" className="sale">
                      <Trans id="Sum" />
                    </td>
                    <td align="right">
                      <Trans id="Number of Shares after Transaction" />
                    </td>
                  </tr>
                </thead>
                <tbody>
                  {sc.transactions.map((row, index) => (
                    <tr key={index}>
                      <td>
                        <DateFormat locale={i18n.locale}>{row.date}</DateFormat>
                      </td>
                      <td>{i18n.locale === 'sv' ? row.name.primaryLang : row.name.secondaryLang}</td>
                      <td align="right">
                        <NumberFormat locale={i18n.locale} maximumFractionDigits={0}>
                          {row.numberOfShares}
                        </NumberFormat>
                      </td>
                      <td align="right" className="buy">
                        <NumberFormat variant="currency" currency={row.currency} locale={i18n.locale}>
                          {row.buy?.pricePerShare}
                        </NumberFormat>
                      </td>
                      <td align="right" className="buy">
                        <NumberFormat variant="currency" currency={row.currency} locale={i18n.locale}>
                          {row.buy?.totalSum}
                        </NumberFormat>
                      </td>
                      <td align="right" className="sale">
                        <NumberFormat variant="currency" currency={row.currency} locale={i18n.locale}>
                          {row.sell?.pricePerShare}
                        </NumberFormat>
                      </td>
                      <td align="right" className="sale">
                        <NumberFormat variant="currency" currency={row.currency} locale={i18n.locale}>
                          {row.sell?.totalSum}
                        </NumberFormat>
                      </td>
                      <td align="right">
                        <NumberFormat maximumFractionDigits={0} locale={i18n.locale}>
                          {row.totalNumberOfShares}
                        </NumberFormat>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  {sc.sums.map((sum, i) => (
                    <tr key={i}>
                      {i === 0 ? (
                        <th colSpan={3}>
                          <Trans id="Total" />
                          {sc.splitExists && <Trans id=" (Average price per share is split adjusted):" />}
                        </th>
                      ) : (
                        <td colSpan={3} />
                      )}
                      <td align="right" className="buy">
                        <NumberFormat variant="currency" currency={sum.currency} locale={i18n.locale}>
                          {sum.averagePricePerShareAcquired}
                        </NumberFormat>
                      </td>
                      <td align="right" className="buy">
                        <NumberFormat variant="currency" currency={sum.currency} locale={i18n.locale}>
                          {sum.totalPricePurchase}
                        </NumberFormat>
                      </td>
                      <td align="right" className="sale">
                        <NumberFormat variant="currency" currency={sum.currency} locale={i18n.locale}>
                          {sum.averagePricePerDivested}
                        </NumberFormat>
                      </td>
                      <td align="right" className="sale">
                        <NumberFormat variant="currency" currency={sum.currency} locale={i18n.locale}>
                          {sum.totalPriceSale}
                        </NumberFormat>
                      </td>
                      <td />
                    </tr>
                  ))}
                </tfoot>
              </Table>
            </Stack>
          </Sheet>
        );
      })}
    </Stack>
  );
};
